import { supabase } from '@/lib/supabase';
import { OpenItem } from '@/types/models/OpenItem';

// Utility to run promise-returning functions in batches with concurrency limit
async function batchPromisesWithConcurrency<T>(tasks: (() => Promise<T>)[], concurrency: number): Promise<T[]> {
  const results: T[] = [];
  let index = 0;
  async function worker() {
    while (index < tasks.length) {
      const current = index++;
      results[current] = await tasks[current]();
    }
  }
  const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

export const OpenItemService = {
  async getAll(): Promise<OpenItem[]> {
    const pageSize = 1000;

    // Step 1: Get the total count
    const { count, error: countError } = await supabase
      .from('open_items')
      .select('*', { count: 'exact', head: true });

    if (countError) throw countError;
    if (!count || count === 0) return [];

    // Step 2: Calculate number of pages
    const numPages = Math.ceil(count / pageSize);

    // Step 3: Create an array of page fetch functions
    const pageFetchers = Array.from({ length: numPages }, (_, i) => async () => {
      const from = i * pageSize;
      const to = from + pageSize - 1;
      const { data, error } = await supabase
        .from('open_items')
        .select('*')
        .range(from, to);
      if (error) throw error;
      return data as OpenItem[];
    });

    // Step 4: Use the reusable batch utility with concurrency 10
    const results = await batchPromisesWithConcurrency(pageFetchers, 10);

    // Step 5: Flatten the results
    return results.flat();
  },
};